import { API_BASE_URL } from "src/config/apiBase";
import React, { useState, useMemo, useEffect } from "react";
import {
    Newspaper,
    Search,
    Plus,
    ImageIcon,
    Eye,
    PenLine,
    Trash2,
    Copy,
    RotateCcw,
} from "lucide-react";
import EmptyStateBlock from "../EmptyStateBlock";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import slugify from "./slugify";
import { decryptToken } from "../utils/getToken";
import { getDecryptedUserFromCookie } from "../utils/getCookie";
import OmsSpinnerData from "../Loader/Spinner";
import "./AllBlogs.css";




const formatDate = (iso) => {
    try {
        const d = new Date(iso);
        return d.toLocaleString(undefined, {
            year: "numeric",
            month: "short",
            day: "numeric",
            hour: "2-digit",
            minute: "2-digit",
            second: "2-digit",
            hour12: false, // 24-hour format; set true if you want AM/PM
        });
    } catch {
        return iso;
    }
};


const months = [
    "All Months",
    "January",
    "February",
    "March",
    "April",
    "May",
    "June",
    "July",
    "August",
    "September",
    "October",
    "November",
    "December",
];

const AllBlogs = () => {
    const [selected, setSelected] = useState([]);
    const [categories, setCategories] = useState([]);
    const [activeTab, setActiveTab] = useState("All");
    const [search, setSearch] = useState("");
    const [selectedMonth, setSelectedMonth] = useState("All Months");
    const [entriesPerPage, setEntriesPerPage] = useState(5); // Default entries per page
    const [currentPage, setCurrentPage] = useState(1); // Track current page
    const url = API_BASE_URL
    const [blogs, setBlogs] = useState([]);
    const [status, setStatus] = useState("");
    const [selectedCategory, setSelectedCategory] = useState("");
    const navigate = useNavigate();
    const token = decryptToken("accessToken") || ""
    const isLoggedIn = getDecryptedUserFromCookie("gta_mainstateuser");
    const [load, setLoad] = useState(false);
    const [fetchError, setFetchError] = useState("");
    const [analytics, setAnalytics] = useState({ totalViews: 0, pendingComments: 0 });
    const stats = useMemo(() => {
        const active = blogs.filter((b) => !b.trash);
        return {
            total: active.length,
            published: active.filter((b) => b.status === "Publish").length,
            draft: active.filter((b) => b.status !== "Publish").length,
            trashed: blogs.filter((b) => b.trash).length,
        };
    }, [blogs]);

    const isPublished = (status) =>
        status === "Publish" || status?.toLowerCase() === "publish";




    const handleBulkTrash = async () => {
        if (selected.length === 0) return;
        if (!window.confirm("Move selected categories to trash?")) return;

        try {
            const response = await fetch(`${url}/blog/bulk/trash`, {
                method: "PATCH",
                headers: {
                    "Content-Type": "application/json",
                    "Authorization": `Bearer ${token}`,
                },
                body: JSON.stringify({ ids: selected, trash: true }),
            });

            if (response.ok) {
                setBlogs((prev) =>
                    prev.map((cat) =>
                        selected.includes(cat.id)
                            ? { ...cat, trash: true }
                            : cat
                    )
                );
                setSelected([]);
                toast.success("Selected posts moved to trash");
            } else {
                const err = await response.json();
                console.error("Bulk trash error:", err.message);
            }
        } catch (error) {
            console.error("Error bulk trashing categories:", error);
        }
    };

    const handleBulkRestore = async () => {
        if (selected.length === 0) return;
        if (!window.confirm("Restore selected categories from trash?")) return;

        try {
            const response = await fetch(`${url}/blog/bulk/trash`, {
                method: "PATCH",
                headers: {
                    "Content-Type": "application/json",
                    "Authorization": `Bearer ${token}`,
                },
                body: JSON.stringify({ ids: selected, trash: false }),
            });

            if (response.ok) {
                setBlogs((prev) =>
                    prev.map((cat) =>
                        selected.includes(cat.id)
                            ? { ...cat, trash: false }
                            : cat
                    )
                );
                setSelected([]);
            } else {
                const err = await response.json();
                console.error("Bulk restore error:", err.message);
            }
        } catch (error) {
            console.error("Error bulk restoring categories:", error);
        }
    };

    const handleDuplicate = async (blogId) => {
        try {
            const response = await fetch(`${url}/blog/${blogId}/duplicate`, {
                method: "POST",
                headers: { Authorization: `Bearer ${token}` },
            });
            const data = await response.json();
            if (response.ok && data.data?.id) {
                toast.success("Post duplicated as draft");
                navigate(`/edit-blog/${data.data.id}`);
            } else {
                toast.error(data.message || "Could not duplicate post");
            }
        } catch (error) {
            toast.error("Could not duplicate post");
            console.error(error);
        }
    };

    const handleDelete = async (id) => {
        if (!window.confirm("Are you sure you want to permanently delete this blog?")) return;

        try {
            const response = await fetch(`${url}/blog/${id}`, {
                method: "DELETE",
                headers: {
                    "Authorization": `Bearer ${token}`
                }
            });

            if (response.ok) {
                setBlogs((prev) => prev.filter((cat) => cat.id !== id));
            } else {
                const err = await response.json();
                console.error("Error deleting category:", err.message);
            }
        } catch (error) {
            console.error("Error deleting category:", error);
        }
    };



    const handleTrash = async (id) => {
        try {
            const response = await fetch(`${url}/blog/trash/${id}`, {
                method: "PATCH",
                headers: {
                    "Content-Type": "application/json",
                    "Authorization": `Bearer ${token}`
                },
                body: JSON.stringify({ trash: true }),
            });

            if (response.ok) {
                setBlogs((prev) =>
                    prev.map((item) =>
                        item.id === id
                            ? { ...item, trash: true }
                            : item
                    )
                );
            } else {
                const err = await response.json();
                console.error("Error:", err.message);
            }
        } catch (error) {
            console.error("Error trashing category:", error);
        }
    }

    // 🧹 Bulk Delete
    const handleBulkDelete = async () => {
        if (selected.length === 0) return;
        if (!window.confirm("Are you sure you want to permanently delete selected categories?")) return;

        try {
            const response = await fetch(`${url}/blog/bulk`, {
                method: "DELETE",
                headers: {
                    "Content-Type": "application/json",
                    "Authorization": `Bearer ${token}`
                },
                body: JSON.stringify({ ids: selected }),
            });

            if (response.ok) {
                setBlogs((prev) =>
                    prev.filter((cat) => !selected.includes(cat.id))
                );
                setSelected([]);
            } else {
                const err = await response.json();
                console.error("Error bulk deleting:", err.message);
            }
        } catch (error) {
            console.error("Error bulk deleting categories:", error);
        }
    };


    const handleRestore = async (id) => {
        try {
            const response = await fetch(`${url}/blog/trash/${id}`, {
                method: "PATCH",
                headers: {
                    "Content-Type": "application/json",
                    "Authorization": `Bearer ${token}`
                },
                body: JSON.stringify({ trash: false }),
            });

            if (response.ok) {
                setBlogs((prev) =>
                    prev.map((item) =>
                        item.id === id
                            ? { ...item, trash: false }
                            : item
                    )
                );
            }
        } catch (error) {
            console.error("Error restoring category:", error);
        }
    }

    const fetchBlogs = async () => {
        setLoad(true);
        setFetchError("");
        try {
            const response = await fetch(`${url}/blog?admin=true`, {
                method: "GET",
                headers: {
                    "Authorization": `Bearer ${token}`
                }
            });
            const res = await response.json();
            if (res.success) {
                const mapped = (res.data || []).map((b) => ({
                    id: b.id,
                    trash: b.trash,
                    title: b.title,
                    author_name: b.author_name || "N/A",
                    date: b.createdAt,
                    status: b.status,
                    categories: (b.categories || []).map(
                        (c) =>
                            c.categoryDetails?.name +
                            (c.categoryDetails?.parentDetails
                                ? ` (${c.categoryDetails.parentDetails.name})`
                                : "")
                    ),
                    feature_image: b.feature_image,
                    slug: b.slug,
                    sections: b.sections,
                    visibility: b.visibility,
                    password: b.password,
                    tags: (b.tags || []).map((t) => t.tagDetails?.name),
                }));
                setBlogs(mapped);
            } else {
                const msg = res.message || "Could not load blog posts.";
                setFetchError(msg);
                toast.error(msg);
            }
        } catch (error) {
            const offline =
                error?.message === "Failed to fetch" ||
                error?.name === "TypeError";
            const msg = offline
                ? `Cannot reach the API at ${url}. Start the Backend server (port 5000) or check REACT_APP_LINK in Admin/.env.`
                : error?.message || "Could not load blog posts.";
            setFetchError(msg);
            toast.error(msg, { autoClose: 6000 });
            console.error(error);
        } finally {
            setLoad(false);
        }
    };

    const fetchDataCat = async () => {
        try {
            const response = await fetch(`${url}/blog-category`, {
                method: "GET",
                headers: {
                    "Authorization": `Bearer ${token}`
                }
            });
            const res = await response.json();
            setCategories(res.data);
        } catch (error) {
            console.log(error);
        }
    }

    

    const toggleBlogStatus = async (blogId) => {
        try {
            const response = await fetch(`${url}/blog/toggle/${blogId}`, {
                method: "PATCH",
                headers: {
                    "Content-Type": "application/json",
                    "Authorization": `Bearer ${token}`
                },
            });

            const resData = await response.json();

            if (response.ok) {
                toast.success(resData.message, {
                    position: "top-center",
                    autoClose: 1500,
                });

                setBlogs((prev) =>
                    prev.map((b) =>
                        b.id === blogId
                            ? { ...b, status: resData.data.status, trash: resData.data.trash }
                            : b
                    )
                );
            } else {
                toast.error(resData.message || "Failed to update status");
            }
        } catch (err) {
            console.error(err);
            toast.error("Something went wrong");
        }
    };



    const filteredBlogs = blogs.filter((b) => {
        const matchSearch = b.title.toLowerCase().includes(search.toLowerCase());

        const blogMonth = new Date(b.date).toLocaleString("default", { month: "long" });
        const matchMonth = selectedMonth === "All Months" || blogMonth === selectedMonth;

        const matchTab =
            (activeTab === "All" && !b.trash) ||
            (activeTab === "Published" && isPublished(b.status) && !b.trash) ||
            (activeTab === "Drafts" && !isPublished(b.status) && !b.trash) ||
            (activeTab === "Trashed" && b.trash);

        const matchCategory =
            !selectedCategory ||
            b.categories.some(
                (c) => c === selectedCategory || c.includes(`(${selectedCategory})`)
            );

        return matchSearch && matchMonth && matchTab && matchCategory;
    });



    const allSelected = filteredBlogs.length > 0 && filteredBlogs.every((cat) => selected.includes(cat.id));
    const someSelected = filteredBlogs.some((cat) => selected.includes(cat.id)) && !allSelected;

    const toggleSelectAll = () => {
        if (allSelected) {
            setSelected([]); // Deselect all
        } else {
            setSelected(filteredBlogs.map((cat) => cat.id)); // Select all visible categories
        }
    };

    const toggleSelectOne = (id) => {
        if (selected.includes(id)) {
            setSelected(selected.filter((selectedId) => selectedId !== id));
        } else {
            setSelected([...selected, id]);
        }
    };

    // Pagination logic
    const totalPages = Math.ceil(filteredBlogs.length / entriesPerPage);
    const paginatedBlogs = filteredBlogs.slice(0, currentPage * entriesPerPage);

    const handleLoadMore = () => {
        if (currentPage < totalPages) {
            setCurrentPage((prev) => prev + 1);
        }
    };

    

    useEffect(() => {
        fetchBlogs();
        fetchDataCat();
        const fetchAnalytics = async () => {
            try {
                const res = await fetch(`${url}/blog/analytics/summary`, {
                    headers: { Authorization: `Bearer ${token}` },
                });
                const data = await res.json();
                if (data.success && data.data) setAnalytics(data.data);
            } catch {
                /* optional */
            }
        };
        fetchAnalytics();
    }, [])

    return (
        <>
            {load && <OmsSpinnerData />}
            <div className="blog-hub">
                <header className="blog-hub-hero">
                    <div className="blog-hub-hero-inner">
                        <div className="blog-hub-title-block">
                            <h1>Content Studio</h1>
                            <p>Manage posts, drafts, and your publishing pipeline.</p>
                            <div className="blog-hub-stats">
                                <span className="blog-hub-stat">
                                    Total <strong>{stats.total}</strong>
                                </span>
                                <span className="blog-hub-stat blog-hub-stat--live">
                                    Live <strong>{stats.published}</strong>
                                </span>
                                <span className="blog-hub-stat blog-hub-stat--draft">
                                    Drafts <strong>{stats.draft}</strong>
                                </span>
                                <span className="blog-hub-stat blog-hub-stat--trash">
                                    Trash <strong>{stats.trashed}</strong>
                                </span>
                                <span className="blog-hub-stat">
                                    Views <strong>{analytics.totalViews ?? 0}</strong>
                                </span>
                                <button
                                    type="button"
                                    className="blog-hub-stat blog-hub-stat--draft"
                                    onClick={() => navigate("/comment")}
                                    title="Open comment moderation"
                                >
                                    Pending comments <strong>{analytics.pendingComments ?? 0}</strong>
                                </button>
                            </div>
                        </div>
                        <button
                            type="button"
                            className="blog-hub-cta"
                            onClick={() => navigate("/add-blog")}
                        >
                            <Plus size={18} strokeWidth={2.5} />
                            New post
                        </button>
                    </div>
                </header>

                <div className="blog-hub-body">
                    {fetchError && (
                        <div
                            className="mb-4 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800"
                            role="alert"
                        >
                            {fetchError}
                        </div>
                    )}
                    <div className="blog-hub-toolbar">
                        <div className="blog-hub-tabs">
                            <button
                                type="button"
                                className={`blog-hub-tab ${activeTab === "All" ? "is-active" : ""}`}
                                onClick={() => setActiveTab("All")}
                            >
                                All
                            </button>
                            <button
                                type="button"
                                className={`blog-hub-tab ${activeTab === "Published" ? "is-active" : ""}`}
                                onClick={() => setActiveTab("Published")}
                            >
                                Published
                            </button>
                            <button
                                type="button"
                                className={`blog-hub-tab ${activeTab === "Drafts" ? "is-active" : ""}`}
                                onClick={() => setActiveTab("Drafts")}
                            >
                                Drafts
                            </button>
                            <button
                                type="button"
                                className={`blog-hub-tab ${activeTab === "Trashed" ? "is-active--trash is-active" : ""}`}
                                onClick={() => setActiveTab("Trashed")}
                            >
                                Trashed
                            </button>
                        </div>
                        <div className="blog-hub-search-wrap">
                            <Search size={18} />
                            <input
                                type="text"
                                className="blog-hub-search"
                                placeholder="Search posts…"
                                value={search}
                                onChange={(e) => setSearch(e.target.value)}
                            />
                        </div>
                    </div>

                    <div className="blog-hub-filters">
                        <select
                            className="blog-hub-select"
                            value={entriesPerPage}
                            onChange={(e) => {
                                setEntriesPerPage(Number(e.target.value));
                                setCurrentPage(1);
                            }}
                        >
                            <option value={5}>5 per page</option>
                            <option value={10}>10 per page</option>
                            <option value={20}>20 per page</option>
                        </select>
                        <select
                            className="blog-hub-select"
                            value={selectedMonth}
                            onChange={(e) => {
                                setSelectedMonth(e.target.value);
                                setCurrentPage(1);
                            }}
                        >
                            {months.map((month) => (
                                <option key={month} value={month}>{month}</option>
                            ))}
                        </select>
                        <select
                            className="blog-hub-select"
                            value={selectedCategory}
                            onChange={(e) => {
                                setSelectedCategory(e.target.value);
                                setCurrentPage(1);
                            }}
                        >
                            <option value="">All categories</option>
                            {categories.map((cat) => (
                                <option key={cat.id} value={cat.name}>{cat.name}</option>
                            ))}
                        </select>
                        {activeTab === "Trashed" ? (
                            <>
                                <button
                                    type="button"
                                    disabled={selected.length === 0}
                                    onClick={handleBulkDelete}
                                    className="blog-hub-bulk-btn blog-hub-bulk-btn--danger"
                                >
                                    Delete selected
                                </button>
                                <button
                                    type="button"
                                    disabled={selected.length === 0}
                                    onClick={handleBulkRestore}
                                    className="blog-hub-bulk-btn blog-hub-bulk-btn--primary"
                                >
                                    Restore selected
                                </button>
                            </>
                        ) : (
                            <button
                                type="button"
                                disabled={selected.length === 0}
                                onClick={handleBulkTrash}
                                className="blog-hub-bulk-btn blog-hub-bulk-btn--danger"
                            >
                                Trash selected
                            </button>
                        )}
                    </div>

                    <p className="blog-hub-meta-line">
                        Showing {paginatedBlogs.length} of {filteredBlogs.length} posts
                    </p>

                    {filteredBlogs.length > 0 && (
                        <label className="blog-hub-select-all">
                            <input
                                type="checkbox"
                                checked={allSelected}
                                ref={(el) => {
                                    if (el) el.indeterminate = someSelected;
                                }}
                                onChange={toggleSelectAll}
                                aria-label="Select all visible posts"
                            />
                            Select all on this page
                        </label>
                    )}

                    <div className="blog-hub-grid">
                        {paginatedBlogs.map((blog) => {
                            const checked = selected.includes(blog.id);
                            const published = isPublished(blog.status);
                            const coverSrc = blog.feature_image
                                ? `${url}${blog.feature_image}`
                                : null;

                            return (
                                <article
                                    key={blog.id}
                                    className={`blog-hub-card ${checked ? "is-selected" : ""}`}
                                >
                                    <input
                                        type="checkbox"
                                        className="blog-hub-card-check"
                                        checked={checked}
                                        onChange={() => toggleSelectOne(blog.id)}
                                        aria-label={`Select ${blog.title}`}
                                    />
                                    <div className="blog-hub-card-cover">
                                        {coverSrc ? (
                                            <img src={coverSrc} alt="" />
                                        ) : (
                                            <div className="blog-hub-card-cover-placeholder">
                                                <ImageIcon size={40} strokeWidth={1.25} />
                                            </div>
                                        )}
                                        <div className="blog-hub-card-badges">
                                            <span
                                                className={`blog-hub-badge ${published ? "blog-hub-badge--live" : "blog-hub-badge--draft"}`}
                                            >
                                                {published ? "Live" : "Draft"}
                                            </span>
                                            {blog.visibility !== "Public" && (
                                                <span className="blog-hub-badge blog-hub-badge--private">
                                                    {blog.visibility}
                                                </span>
                                            )}
                                        </div>
                                    </div>
                                    <div className="blog-hub-card-body">
                                        <h2 className="blog-hub-card-title">{blog.title}</h2>
                                        <p className="blog-hub-card-meta">
                                            {blog.author_name || "No author"} · {formatDate(blog.date)}
                                        </p>
                                        {blog.categories.length > 0 && (
                                            <div className="blog-hub-card-tags">
                                                {blog.categories.slice(0, 3).map((c, i) => (
                                                    <span key={i} className="blog-hub-tag">{c}</span>
                                                ))}
                                                {blog.categories.length > 3 && (
                                                    <span className="blog-hub-tag">
                                                        +{blog.categories.length - 3}
                                                    </span>
                                                )}
                                            </div>
                                        )}
                                        <div className="blog-hub-card-footer">
                                            {!blog.trash && (
                                                <label className="blog-hub-toggle">
                                                    <input
                                                        type="checkbox"
                                                        checked={published}
                                                        onChange={() => toggleBlogStatus(blog.id)}
                                                        aria-label={published ? "Move to draft" : "Publish"}
                                                    />
                                                    {published ? "Published" : "Draft"}
                                                </label>
                                            )}
                                            <div className="blog-hub-actions">
                                                <button
                                                    type="button"
                                                    className="blog-hub-action"
                                                    onClick={() => handleDuplicate(blog.id)}
                                                >
                                                    <Copy size={12} className="inline mr-0.5" />
                                                    Duplicate
                                                </button>
                                                <button
                                                    type="button"
                                                    className="blog-hub-action"
                                                    onClick={() => navigate(`/edit-blog/${blog.id}`)}
                                                >
                                                    <PenLine size={12} className="inline mr-0.5" />
                                                    Edit
                                                </button>
                                                <button
                                                    type="button"
                                                    className="blog-hub-action"
                                                    onClick={() =>
                                                        navigate(
                                                            `/admin/preview-blog/${slugify(blog.slug)}/${blog.id}`
                                                        )
                                                    }
                                                >
                                                    <Eye size={12} className="inline mr-0.5" />
                                                    View
                                                </button>
                                                {blog.trash ? (
                                                    <>
                                                        <button
                                                            type="button"
                                                            className="blog-hub-action"
                                                            onClick={() => handleRestore(blog.id)}
                                                        >
                                                            <RotateCcw size={12} className="inline mr-0.5" />
                                                            Restore
                                                        </button>
                                                        <button
                                                            type="button"
                                                            className="blog-hub-action blog-hub-action--danger"
                                                            onClick={() => handleDelete(blog.id)}
                                                        >
                                                            <Trash2 size={12} className="inline mr-0.5" />
                                                            Delete
                                                        </button>
                                                    </>
                                                ) : (
                                                    <button
                                                        type="button"
                                                        className="blog-hub-action blog-hub-action--danger"
                                                        onClick={() => handleTrash(blog.id)}
                                                    >
                                                        <Trash2 size={12} className="inline mr-0.5" />
                                                        Trash
                                                    </button>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                </article>
                            );
                        })}

                        {paginatedBlogs.length === 0 && (
                            <div className="blog-hub-empty">
                                <EmptyStateBlock
                                    compact
                                    Icon={Newspaper}
                                    title="No blog post found"
                                    description={
                                        blogs.length === 0
                                            ? "You have not created any blog posts yet. Add a post to publish content on your site."
                                            : "No posts match the current tab, month, category, or search. Try changing filters or clearing the search box."
                                    }
                                />
                            </div>
                        )}
                    </div>

                    {paginatedBlogs.length < filteredBlogs.length && (
                        <div className="blog-hub-load-more">
                            <button type="button" onClick={handleLoadMore}>
                                Load more posts
                            </button>
                        </div>
                    )}
                </div>
            </div>
        </>
    );
};

export default AllBlogs;